import api from './api';

// ============================================================
// Statistik-Explorer Service
// Alle Interfaces sind vollständig nullable (Backwards Compatibility).
// Alle Responses werden defensiv geparst: Wrapper { status, data } wird
// geprüft, fehlende Felder führen NIEMALS zu Crashes.
// ============================================================

// ------------------------------------------------------------
// Interfaces: Spieler
// ------------------------------------------------------------

export interface StatsPlayerCountry {
  id?: number | string | null;
  name?: string | null;
}

/** Spieler aus /api/players/search bzw. /api/pro/players */
export interface StatsSearchPlayer {
  _id?: string | null;
  flashliveId?: string | null;
  name?: string | null;
  shortName?: string | null;
  slug?: string | null;
  country?: StatsPlayerCountry | string | null;
  image?: string | null;
  currentRanking?: number | null;
  stats?: {
    totalMatches?: number | null;
    wins?: number | null;
    losses?: number | null;
    winRate?: number | null;
  } | null;
}

/** Spieler-Stammdaten aus /api/players/:id/profile (FlashLive-Transform) */
export interface StatsPlayerData {
  flashliveId?: string | null;
  name?: string | null;
  shortName?: string | null;
  countryId?: number | string | null;
  countryName?: string | null;
  image?: string | null;
  birthday?: string | null;
  ranking?: number | null;
  rankingText?: string | null;
  sportId?: number | null;
}

export interface StatsMatchSide {
  name?: string | null;
  shortName?: string | null;
  slug?: string | null;
  score?: number | null;
  countryName?: string | null;
  image?: string | null;
}

/** Ein historisches Ergebnis / kommendes Spiel eines Spielers */
export interface StatsPlayerMatch {
  eventId?: string | null;
  tournamentName?: string | null;
  tournamentImage?: string | null;
  startTime?: string | null;
  round?: string | null;
  home?: StatsMatchSide | null;
  away?: StatsMatchSide | null;
  winner?: number | string | null; // 1 = home, 2 = away
  status?: string | null;
}

export interface StatsPlayerProfile {
  player?: StatsPlayerData | null;
  results?: StatsPlayerMatch[];
  fixtures?: StatsPlayerMatch[];
}

export interface StatsPlayerMatchesPage {
  matches: StatsPlayerMatch[];
  page: number;
  hasMore: boolean;
}

// ------------------------------------------------------------
// Interfaces: Explorer (Turnier-Archiv)
// ------------------------------------------------------------

export interface ExplorerSeason {
  seasonName?: string | null;
  seasonId?: string | null;
  seasonTournamentStageId?: string | null;
}

export interface ExplorerTournament {
  _id?: string | null;
  flashliveId?: string | null;
  name?: string | null;
  country?: string | null;
  image?: string | null;
  currentWinner?: string | null;
  yearSeasons?: ExplorerSeason[];
  seasons?: ExplorerSeason[];
  totalSeasons?: number | null;
}

export interface ExplorerSeasonResult {
  eventId?: string | null;
  startTime?: string | null;
  round?: string | null;
  roundId?: string | number | null;
  home?: StatsMatchSide | null;
  away?: StatsMatchSide | null;
  winner?: number | null;
  status?: string | null;
  homeAverage?: string | number | null;
  awayAverage?: string | number | null;
}

export interface ExplorerSeasonRound {
  name?: string | null;
  matches?: ExplorerSeasonResult[];
}

export interface ExplorerSeasonResultsResponse {
  results: ExplorerSeasonResult[];
  rounds: ExplorerSeasonRound[];
  tournamentFlashliveId?: string | null;
  totalMatches?: number | null;
}

export interface SeasonPlayerStat {
  name?: string | null;
  image?: string | null;
  matches?: number | null;
  wins?: number | null;
  total180s?: number | null;
  total140Plus?: number | null;
  total100Plus?: number | null;
  highestCheckout?: number | null;
  average?: string | number | null;
  bestMatchAverage?: number | null;
  bestMatchAverageOpponent?: string | null;
  setsOrLegsWon?: number | null;
  highFinishes?: number | null;
  highFinishList?: number[] | null;
}

export interface SeasonHighFinishEntry {
  value?: number | null;
  count?: number | null;
}

export interface SeasonStats {
  totalMatches?: number | null;
  totalSetsOrLegs?: number | null;
  total180s?: number | null;
  total140Plus?: number | null;
  total100Plus?: number | null;
  totalHighFinishes?: number | null;
  highestCheckout?: { value?: number | null; player?: string | null; eventId?: string | null } | null;
  highestAverage?: { value?: number | null; player?: string | null; eventId?: string | null; opponent?: string | null } | null;
  matchMode?: string | null;
  rankings?: {
    most180s?: SeasonPlayerStat[];
    highest140Plus?: SeasonPlayerStat[];
    highestCheckouts?: SeasonPlayerStat[];
    // Backend liefert bestTournamentAverages/bestMatchAverages,
    // der ursprüngliche Contract nannte bestAverages – beides unterstützen:
    bestAverages?: SeasonPlayerStat[];
    bestTournamentAverages?: SeasonPlayerStat[];
    bestMatchAverages?: SeasonPlayerStat[];
    mostHighFinishes?: SeasonPlayerStat[];
  } | null;
  playerStats?: Record<string, SeasonPlayerStat> | null;
  highFinishList?: SeasonHighFinishEntry[] | null;
}

export interface DrawSide {
  name?: string | null;
  shortName?: string | null;
  flashliveId?: string | null;
  seed?: string | null;
  score?: number | string | null;
  isWinner?: boolean | null;
  potentialPlayers?: string[] | null;
}

export interface DrawMatch {
  roundNumber?: number | null;
  roundName?: string | null;
  eventId?: string | null;
  startTime?: string | null;
  home?: DrawSide | null;
  away?: DrawSide | null;
  status?: string | null;
  winnerName?: string | null;
}

export interface DrawRound {
  roundNumber?: number | null;
  roundName?: string | null;
  matches?: DrawMatch[];
}

export interface SeasonDraw {
  rounds?: DrawRound[];
  totalParticipants?: number | null;
  totalRounds?: number | null;
  tournamentName?: string | null;
  seasonName?: string | null;
}

// ------------------------------------------------------------
// Interfaces: MODUS Super Series (MSS / DartConnect)
// Contract – Backend wird parallel gebaut. Bei 404/Fehler → null,
// Frontend blendet die Sektion dann komplett aus.
// ------------------------------------------------------------

export interface MssSeriesStat {
  seriesName?: string | null;
  matches?: number | null;
  wins?: number | null;
  avgAverage?: number | null;
  total180s?: number | null;
}

export interface MssPlayerStats {
  matches?: number | null;
  wins?: number | null;
  losses?: number | null;
  winRate?: number | null;
  total180s?: number | null;
  bestAverage?: number | null;
  avgAverage?: number | null;
  highestCheckout?: number | null;
  checkouts100plus?: number | null;
  perSeries?: MssSeriesStat[] | null;
}

export interface MssLink {
  externalName?: string | null;
  confidence?: number | string | null;
  verified?: boolean | null;
}

export interface MssPlayerData {
  linked?: boolean | null;
  link?: MssLink | null;
  stats?: MssPlayerStats | null;
}

export interface MssMatch {
  gameId?: string | null;
  startTime?: string | null;
  seriesName?: string | null;
  groupName?: string | null;
  opponentName?: string | null;
  playerLegs?: number | null;
  opponentLegs?: number | null;
  won?: boolean | null;
  playerStats?: {
    average?: number | null;
    turns180?: number | null;
    highestCheckout?: number | null;
  } | null;
}

export interface MssMatchesPage {
  matches: MssMatch[];
  page: number;
  totalPages: number;
  total: number;
}

export interface MssSeries {
  _id?: string | null;
  id?: number | string | null;
  name?: string | null;
  seriesName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  matches?: number | null;
  totalMatches?: number | null;
  players?: number | null;
  totalPlayers?: number | null;
}

/** Normalisierter Leaderboard-Eintrag einer MSS-Serie */
export interface MssLeaderboardEntry {
  name: string;
  value: number;
  matches?: number | null;
  flashliveId?: string | null;
}

export interface MssSeriesOverview {
  topAverages: MssLeaderboardEntry[];
  top180s: MssLeaderboardEntry[];
  topCheckouts: MssLeaderboardEntry[];
}

// ============================================================
// Service
// ============================================================

class StatsExplorerService {
  /**
   * Entpackt { status, data } Wrapper defensiv.
   * Akzeptiert auch Responses ohne Wrapper.
   */
  private unwrap(response: any): any {
    const payload = response?.data;
    if (payload == null) return null;
    if (payload.data !== undefined) return payload.data;
    return payload;
  }

  private safeArray<T>(value: any): T[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v) => v != null && typeof v === 'object') as T[];
  }

  private toNumber(value: any): number | null {
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string') {
      const n = parseFloat(value.replace(',', '.').replace(/[^0-9.-]/g, ''));
      if (!isNaN(n)) return n;
    }
    return null;
  }

  // ------------------------------------------------------------
  // Spieler
  // ------------------------------------------------------------

  async searchPlayers(query: string, limit: number = 20): Promise<StatsSearchPlayer[]> {
    const q = (query || '').trim();
    if (q.length < 2) return [];
    try {
      const res = await api.get('/api/players/search', { params: { q, limit } });
      const data = this.unwrap(res);
      return this.safeArray<StatsSearchPlayer>(data);
    } catch (err) {
      console.error('Fehler bei der Spieler-Suche:', err);
      return [];
    }
  }

  async getPlayerProfile(playerId: string): Promise<StatsPlayerProfile | null> {
    if (!playerId) return null;
    try {
      const res = await api.get(`/api/players/${encodeURIComponent(playerId)}/profile`);
      const data = this.unwrap(res);
      if (!data || typeof data !== 'object') return null;
      return {
        player: data.player && typeof data.player === 'object' ? data.player : null,
        results: this.safeArray<StatsPlayerMatch>(data.results),
        fixtures: this.safeArray<StatsPlayerMatch>(data.fixtures),
      };
    } catch (err) {
      console.error('Fehler beim Laden des Spieler-Profils:', err);
      return null;
    }
  }

  async getPlayerResults(playerId: string, page: number = 1): Promise<StatsPlayerMatchesPage> {
    const empty: StatsPlayerMatchesPage = { matches: [], page, hasMore: false };
    if (!playerId) return empty;
    try {
      const res = await api.get(`/api/players/${encodeURIComponent(playerId)}/results`, { params: { page } });
      const data = this.unwrap(res);
      const matches = this.safeArray<StatsPlayerMatch>(data?.results);
      return {
        matches,
        page: typeof data?.page === 'number' ? data.page : page,
        hasMore: data?.hasMore === true && matches.length > 0,
      };
    } catch (err) {
      console.error('Fehler beim Laden der Spieler-Ergebnisse:', err);
      return empty;
    }
  }

  async getPlayerFixtures(playerId: string, page: number = 1): Promise<StatsPlayerMatchesPage> {
    const empty: StatsPlayerMatchesPage = { matches: [], page, hasMore: false };
    if (!playerId) return empty;
    try {
      const res = await api.get(`/api/players/${encodeURIComponent(playerId)}/fixtures`, { params: { page } });
      const data = this.unwrap(res);
      const matches = this.safeArray<StatsPlayerMatch>(data?.fixtures);
      return {
        matches,
        page: typeof data?.page === 'number' ? data.page : page,
        hasMore: data?.hasMore === true && matches.length > 0,
      };
    } catch (err) {
      console.error('Fehler beim Laden der kommenden Spiele:', err);
      return empty;
    }
  }

  // ------------------------------------------------------------
  // Explorer: Turnier-Archiv
  // ------------------------------------------------------------

  async getExplorerYears(): Promise<number[]> {
    try {
      const res = await api.get('/api/pro/explorer/years');
      const data = this.unwrap(res);
      const years = data?.years;
      if (!Array.isArray(years)) return [];
      return years
        .map((y: any) => (typeof y === 'number' ? y : parseInt(y, 10)))
        .filter((y: number) => !isNaN(y) && y > 1900)
        .sort((a: number, b: number) => b - a);
    } catch (err) {
      console.error('Fehler beim Laden der Jahre:', err);
      return [];
    }
  }

  async getTournamentsByYear(year: number): Promise<ExplorerTournament[]> {
    if (!year) return [];
    try {
      const res = await api.get(`/api/pro/explorer/year/${year}`);
      const data = this.unwrap(res);
      return this.safeArray<ExplorerTournament>(data?.tournaments);
    } catch (err) {
      console.error('Fehler beim Laden der Turniere des Jahres:', err);
      return [];
    }
  }

  async getTournamentSeasons(
    flashliveId: string,
  ): Promise<{ tournament: ExplorerTournament | null; seasons: ExplorerSeason[] }> {
    const empty = { tournament: null, seasons: [] as ExplorerSeason[] };
    if (!flashliveId) return empty;
    try {
      const res = await api.get(`/api/pro/explorer/tournament/${encodeURIComponent(flashliveId)}/seasons`);
      const data = this.unwrap(res);
      return {
        tournament: data?.tournament && typeof data.tournament === 'object' ? data.tournament : null,
        seasons: this.safeArray<ExplorerSeason>(data?.seasons),
      };
    } catch (err) {
      console.error('Fehler beim Laden der Saisons:', err);
      return empty;
    }
  }

  async getSeasonResults(seasonStageId: string): Promise<ExplorerSeasonResultsResponse> {
    const empty: ExplorerSeasonResultsResponse = { results: [], rounds: [] };
    if (!seasonStageId) return empty;
    try {
      const res = await api.get(`/api/pro/explorer/season/${encodeURIComponent(seasonStageId)}/results`);
      const payload: any = res?.data || {};
      const data = payload.data || {};
      return {
        results: this.safeArray<ExplorerSeasonResult>(data.results),
        rounds: this.safeArray<ExplorerSeasonRound>(data.rounds),
        tournamentFlashliveId: payload.tournamentFlashliveId ?? null,
        totalMatches: typeof payload.totalMatches === 'number' ? payload.totalMatches : null,
      };
    } catch (err) {
      console.error('Fehler beim Laden der Saison-Ergebnisse:', err);
      return empty;
    }
  }

  async getSeasonStats(seasonStageId: string): Promise<SeasonStats | null> {
    if (!seasonStageId) return null;
    try {
      const res = await api.get(`/api/pro/explorer/season/${encodeURIComponent(seasonStageId)}/stats`);
      const data = this.unwrap(res);
      if (!data || typeof data !== 'object') return null;
      return data as SeasonStats;
    } catch (err) {
      console.error('Fehler beim Laden der Saison-Statistiken:', err);
      return null;
    }
  }

  async getSeasonDraw(seasonStageId: string): Promise<SeasonDraw | null> {
    if (!seasonStageId) return null;
    try {
      const res = await api.get(`/api/pro/explorer/season/${encodeURIComponent(seasonStageId)}/draw`);
      const payload: any = res?.data || {};
      const data = payload.data;
      if (!data || typeof data !== 'object') return null;
      return {
        rounds: this.safeArray<DrawRound>(data.rounds),
        totalParticipants: typeof data.totalParticipants === 'number' ? data.totalParticipants : null,
        totalRounds: typeof data.totalRounds === 'number' ? data.totalRounds : null,
        tournamentName: payload.tournamentName ?? null,
        seasonName: payload.seasonName ?? null,
      };
    } catch (err) {
      console.error('Fehler beim Laden des Brackets:', err);
      return null;
    }
  }

  // ------------------------------------------------------------
  // MODUS Super Series (MSS) – defensiv: bei Fehlern/404 → null/[]
  // ------------------------------------------------------------

  async getMssPlayerStats(flashliveId: string): Promise<MssPlayerData | null> {
    if (!flashliveId) return null;
    try {
      const res = await api.get(`/api/stats/player/${encodeURIComponent(flashliveId)}/mss`);
      const data = this.unwrap(res);
      if (!data || typeof data !== 'object') return null;
      return {
        linked: data.linked === true,
        link: data.link && typeof data.link === 'object' ? data.link : null,
        stats: data.stats && typeof data.stats === 'object' ? data.stats : null,
      };
    } catch (err) {
      // Endpoint existiert evtl. noch nicht (wird parallel gebaut) → Sektion ausblenden
      return null;
    }
  }

  async getMssPlayerMatches(
    flashliveId: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<MssMatchesPage> {
    const empty: MssMatchesPage = { matches: [], page, totalPages: 0, total: 0 };
    if (!flashliveId) return empty;
    try {
      const res = await api.get(`/api/stats/player/${encodeURIComponent(flashliveId)}/mss/matches`, {
        params: { page, limit },
      });
      const data = this.unwrap(res);
      const matches = this.safeArray<MssMatch>(data?.matches);
      return {
        matches,
        page: typeof data?.page === 'number' ? data.page : page,
        totalPages: typeof data?.totalPages === 'number' ? data.totalPages : 0,
        total: typeof data?.total === 'number' ? data.total : matches.length,
      };
    } catch (err) {
      return empty;
    }
  }

  async getMssSeries(): Promise<MssSeries[]> {
    try {
      const res = await api.get('/api/stats/mss/series');
      const data = this.unwrap(res);
      if (Array.isArray(data)) return this.safeArray<MssSeries>(data);
      return this.safeArray<MssSeries>(data?.series);
    } catch (err) {
      return [];
    }
  }

  /**
   * Lädt das Serien-Overview und normalisiert die Leaderboards defensiv,
   * da der genaue Response-Shape noch nicht final ist.
   */
  async getMssSeriesOverview(seriesId: string): Promise<MssSeriesOverview | null> {
    if (!seriesId) return null;
    try {
      const res = await api.get(`/api/stats/mss/series/${encodeURIComponent(seriesId)}/overview`);
      const data = this.unwrap(res);
      if (!data || typeof data !== 'object') return null;

      const source = data.leaderboards && typeof data.leaderboards === 'object' ? data.leaderboards : data;

      const findList = (patterns: RegExp[]): any[] => {
        for (const key of Object.keys(source)) {
          if (patterns.some((p) => p.test(key)) && Array.isArray(source[key])) {
            return source[key];
          }
        }
        return [];
      };

      const normalize = (list: any[], valueKeys: string[]): MssLeaderboardEntry[] => {
        return this.safeArray<any>(list)
          .map((entry) => {
            const name =
              entry.playerName || entry.externalName || entry.name || entry.player || null;
            let value: number | null = null;
            for (const key of valueKeys) {
              const v = this.toNumber(entry[key]);
              if (v != null) {
                value = v;
                break;
              }
            }
            if (value == null) value = this.toNumber(entry.value);
            if (!name || value == null) return null;
            return {
              name: String(name),
              value,
              matches: this.toNumber(entry.matches),
              flashliveId: entry.flashliveId || entry.flashlivePlayerId || null,
            } as MssLeaderboardEntry;
          })
          .filter((e): e is MssLeaderboardEntry => e != null);
      };

      return {
        topAverages: normalize(findList([/average/i, /avg/i]), ['avgAverage', 'average', 'bestAverage']),
        top180s: normalize(findList([/180/]), ['total180s', 'count180s', 'turns180']),
        topCheckouts: normalize(findList([/checkout/i, /finish/i]), ['highestCheckout', 'checkout', 'bestCheckout']),
      };
    } catch (err) {
      return null;
    }
  }

  // ------------------------------------------------------------
  // Helper
  // ------------------------------------------------------------

  /** Entfernt Klammer-Zusätze wie "(Eng)" am Namensende */
  cleanPlayerName(name?: string | null): string {
    if (!name || typeof name !== 'string') return 'Unbekannt';
    return name.replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Unbekannt';
  }

  getCountryName(country?: StatsPlayerCountry | string | null): string | null {
    if (!country) return null;
    if (typeof country === 'string') return country.trim() || null;
    if (typeof country === 'object' && typeof country.name === 'string') {
      return country.name.trim() || null;
    }
    return null;
  }

  /** Emoji-Flagge aus (deutschem oder englischem) Ländernamen – null wenn unbekannt */
  getFlagEmoji(countryName?: string | null): string | null {
    if (!countryName || typeof countryName !== 'string') return null;
    const key = countryName.trim().toLowerCase();
    const map: Record<string, string> = {
      england: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      schottland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      wales: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
      nordirland: '🇬🇧',
      'northern ireland': '🇬🇧',
      grossbritannien: '🇬🇧',
      großbritannien: '🇬🇧',
      'great britain': '🇬🇧',
      niederlande: '🇳🇱',
      netherlands: '🇳🇱',
      holland: '🇳🇱',
      deutschland: '🇩🇪',
      germany: '🇩🇪',
      österreich: '🇦🇹',
      oesterreich: '🇦🇹',
      austria: '🇦🇹',
      schweiz: '🇨🇭',
      switzerland: '🇨🇭',
      belgien: '🇧🇪',
      belgium: '🇧🇪',
      irland: '🇮🇪',
      ireland: '🇮🇪',
      australien: '🇦🇺',
      australia: '🇦🇺',
      neuseeland: '🇳🇿',
      'new zealand': '🇳🇿',
      kanada: '🇨🇦',
      canada: '🇨🇦',
      usa: '🇺🇸',
      'vereinigte staaten': '🇺🇸',
      'united states': '🇺🇸',
      polen: '🇵🇱',
      poland: '🇵🇱',
      litauen: '🇱🇹',
      lithuania: '🇱🇹',
      lettland: '🇱🇻',
      latvia: '🇱🇻',
      tschechien: '🇨🇿',
      'czech republic': '🇨🇿',
      czechia: '🇨🇿',
      schweden: '🇸🇪',
      sweden: '🇸🇪',
      dänemark: '🇩🇰',
      daenemark: '🇩🇰',
      denmark: '🇩🇰',
      norwegen: '🇳🇴',
      norway: '🇳🇴',
      finnland: '🇫🇮',
      finland: '🇫🇮',
      island: '🇮🇸',
      iceland: '🇮🇸',
      spanien: '🇪🇸',
      spain: '🇪🇸',
      portugal: '🇵🇹',
      italien: '🇮🇹',
      italy: '🇮🇹',
      frankreich: '🇫🇷',
      france: '🇫🇷',
      kroatien: '🇭🇷',
      croatia: '🇭🇷',
      ungarn: '🇭🇺',
      hungary: '🇭🇺',
      slowenien: '🇸🇮',
      slovenia: '🇸🇮',
      slowakei: '🇸🇰',
      slovakia: '🇸🇰',
      griechenland: '🇬🇷',
      greece: '🇬🇷',
      russland: '🇷🇺',
      russia: '🇷🇺',
      ukraine: '🇺🇦',
      belarus: '🇧🇾',
      weissrussland: '🇧🇾',
      japan: '🇯🇵',
      china: '🇨🇳',
      hongkong: '🇭🇰',
      'hong kong': '🇭🇰',
      singapur: '🇸🇬',
      singapore: '🇸🇬',
      philippinen: '🇵🇭',
      philippines: '🇵🇭',
      malaysia: '🇲🇾',
      indien: '🇮🇳',
      india: '🇮🇳',
      südafrika: '🇿🇦',
      suedafrika: '🇿🇦',
      'south africa': '🇿🇦',
      brasilien: '🇧🇷',
      brazil: '🇧🇷',
      argentinien: '🇦🇷',
      argentina: '🇦🇷',
      gibraltar: '🇬🇮',
      luxemburg: '🇱🇺',
      luxembourg: '🇱🇺',
    };
    return map[key] || null;
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '-';
    }
  }

  formatDateTime(value?: string | null): string {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return '-';
      return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
    } catch {
      return '-';
    }
  }

  /** Jahr aus einem Datum extrahieren (für Filter) */
  getYear(value?: string | null): number | null {
    if (!value) return null;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return d.getFullYear();
    } catch {
      return null;
    }
  }

  /**
   * Robuster Namensvergleich. FlashLive liefert je nach Endpoint
   * "Luke Littler", "Littler L." oder "Littler L. (Eng)".
   * Vergleicht exakt, als Substring und token-basiert (Nachname + Initial).
   */
  private nameMatches(nameA?: string | null, nameB?: string | null): boolean {
    const a = this.cleanPlayerName(nameA).toLowerCase();
    const b = this.cleanPlayerName(nameB).toLowerCase();
    if (a === 'unbekannt' || b === 'unbekannt') return false;
    if (a === b) return true;
    if (a.includes(b) || b.includes(a)) return true;

    // Token-basiert: "littler l." vs "luke littler"
    const tokensOf = (s: string) =>
      s
        .replace(/[.,]/g, ' ')
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    const tokensA = tokensOf(a);
    const tokensB = tokensOf(b);
    if (tokensA.length === 0 || tokensB.length === 0) return false;

    // Gemeinsamer "voller" Token (Nachname, >= 3 Zeichen)
    const fullA = tokensA.filter((t) => t.length >= 3);
    const fullB = tokensB.filter((t) => t.length >= 3);
    const shared = fullA.filter((t) => fullB.includes(t));
    if (shared.length === 0) return false;

    // Restliche Tokens (Vornamen/Initialen) müssen per Initial zusammenpassen
    const restA = tokensA.filter((t) => !shared.includes(t));
    const restB = tokensB.filter((t) => !shared.includes(t));
    if (restA.length === 0 || restB.length === 0) return true;
    return restA.some((ra) => restB.some((rb) => ra.charAt(0) === rb.charAt(0)));
  }

  /** Prüft, ob eine Match-Seite zum Spieler gehört (Name ODER Kurzname) */
  private sideIsPlayer(
    side: StatsMatchSide | null | undefined,
    playerName?: string | null,
    playerShortName?: string | null,
  ): boolean {
    if (!side) return false;
    const candidates = [side.name, side.shortName];
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (playerName && this.nameMatches(candidate, playerName)) return true;
      if (playerShortName && this.nameMatches(candidate, playerShortName)) return true;
    }
    return false;
  }

  /**
   * Ermittelt aus Sicht eines Spielers, ob er ein Match gewonnen hat.
   * Gibt null zurück, wenn keine eindeutige Zuordnung möglich ist.
   */
  didPlayerWin(
    match: StatsPlayerMatch | null | undefined,
    playerName?: string | null,
    playerShortName?: string | null,
  ): boolean | null {
    if (!match || (!playerName && !playerShortName)) return null;
    const winner = typeof match.winner === 'string' ? parseInt(match.winner, 10) : match.winner;
    if (winner !== 1 && winner !== 2) return null;

    const isHome = this.sideIsPlayer(match.home, playerName, playerShortName);
    const isAway = this.sideIsPlayer(match.away, playerName, playerShortName);

    if (isHome && !isAway) return winner === 1;
    if (isAway && !isHome) return winner === 2;
    return null;
  }

  /** Liefert den Gegner aus Sicht des Spielers (oder null) */
  getOpponent(
    match: StatsPlayerMatch | null | undefined,
    playerName?: string | null,
    playerShortName?: string | null,
  ): StatsMatchSide | null {
    if (!match || (!playerName && !playerShortName)) return null;
    const isHome = this.sideIsPlayer(match.home, playerName, playerShortName);
    const isAway = this.sideIsPlayer(match.away, playerName, playerShortName);
    if (isHome && !isAway) return match.away || null;
    if (isAway && !isHome) return match.home || null;
    return null;
  }
}

const statsExplorerService = new StatsExplorerService();
export default statsExplorerService;
